import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useStore } from '@nanostores/react';
import { signOut } from 'firebase/auth';

import { startSessionListener, sessionStore } from '@/entities/session';
import { SessionTimeoutHost } from '@/features/session-timeout';
import { auth } from '@/shared';
import { logger } from '@/shared';

import { AuthContext, type AuthContextValue } from './useAuth';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  useEffect(() => {
    logger.info('auth provider mounted', { source: 'app/providers' });
    const stop = startSessionListener();
    return () => stop();
  }, []);

  const session = useStore(sessionStore);
  const isLoading = session.status === 'loading';
  const isAuthenticated = session.status === 'authenticated';

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value: AuthContextValue = useMemo(
    () => ({
      user: session.user,
      isAuthenticated,
      isLoading,
      logout,
    }),
    [isAuthenticated, isLoading, logout, session.user]
  );

  return (
    <AuthContext.Provider value={value}>
      <SessionTimeoutHost />
      {children}
    </AuthContext.Provider>
  );
}
